import styled, { css, type DefaultTheme } from 'styled-components';
import { ellipsis, focusRing, evoLoadAnimation, srOnly } from '@/app/components/search/styles/mixins.ts';
import { IconGlyph } from '@/app/common/icons/MaskIcon';
import { starfishCssUrl } from '@/app/common/icons/starfish.ts';

export type PillState = {
  $selected?: boolean;
  $enabled?: boolean;
  $disabled?: boolean;
  $loading?: boolean;
  $open?: boolean;
  $hideStateIcon?: boolean;
  $clears?: boolean;
};
type PillProps = PillState & { theme: DefaultTheme };

/*
 * The clear control is a sibling of the trigger rather than a child, so the trigger has to claim its
 * hover. Stated as its own rule rather than beside `:hover` in a selector list: an engine without
 * `:has()` drops an entire list it cannot parse, which would take the plain hover with it.
 */
const HOVER_FROM_CLEAR = ':has(~ [data-pill-clear]:hover)';

const getBorderStyles = ({ theme, $selected, $enabled, $disabled, $loading, $open }: PillProps) => {
  const disabled = $loading || $enabled === false;

  let color = theme.kallisto.color.border.static.borderNeutral;
  if ($disabled) color = theme.kallisto.color.border.static.borderDisabled;
  else if ($selected) color = theme.kallisto.color.border.interactive.borderPrimaryDefault;
  else if ($enabled === false) color = theme.kallisto.color.border.static.borderDisabled;

  let hoverColor = theme.kallisto.color.border.interactive.borderNeutralHover;
  if (disabled) hoverColor = theme.kallisto.color.border.static.borderDisabled;
  else if ($selected) hoverColor = theme.kallisto.color.border.interactive.borderPrimaryDefault;

  const hovered = css`
    border-color: ${disabled ? color : hoverColor};
    box-shadow: ${disabled ? 'none' : `inset 0 0 0 1px ${hoverColor}`};
  `;

  return css`
    border: ${$loading ? 'none' : $open ? `1px solid ${hoverColor}` : `1px solid ${color}`};

    /* Outweighs the design system's own pink hover, which it applies to any pill it can click. */
    &:hover {
      ${hovered}
    }

    &${HOVER_FROM_CLEAR} {
      ${hovered}
    }

    ${
      $open &&
      !disabled &&
      css`
        border-color: ${hoverColor};
        box-shadow: inset 0 0 0 1px ${hoverColor};
      `
    }
  `;
};

const getBackgroundStyles = ({ theme, $selected, $enabled, $disabled, $loading, $open }: PillProps) => {
  const disabled = $loading || $enabled === false;

  let color = theme.kallisto.color.common.white['1000'];
  if ($loading || $disabled) color = theme.kallisto.color.background.static.backgroundDisabled;
  else if ($selected) color = theme.kallisto.color.background.interactive.backgroundPrimarySelected;

  let hoverColor = null;
  if (!$selected) hoverColor = theme.kallisto.color.background.interactive.backgroundNeutralHover;

  return css`
    background-color: ${$open && hoverColor ? hoverColor : color};

    ${
      $loading &&
      css`
        position: relative;
        ${evoLoadAnimation};
        background-image: linear-gradient(${color}, ${color});
      `
    }

    /* Always stated: the design system greys any pill it can hover, including one that is inert. */
    &:hover,
    &:focus-visible {
      background-color: ${disabled || !hoverColor ? color : hoverColor};
    }

    &${HOVER_FROM_CLEAR} {
      background-color: ${disabled || !hoverColor ? color : hoverColor};
    }
  `;
};

const getTextStyles = ({ theme, $selected, $enabled, $disabled, $loading }: PillProps) => {
  const disabled = $loading || $enabled === false;

  let color = theme.kallisto.color.text.static.textPrimary;
  if ($disabled) color = theme.kallisto.color.text.static.textDisabled;
  else if ($selected) color = theme.kallisto.color.text.interactive.textPrimaryDefault;
  else if (disabled) color = theme.kallisto.color.text.static.textDisabled;

  return css`
    color: ${color};

    /* The leading icon only: the chevron follows currentColor, and a label's own icons keep theirs. */
    > svg {
      fill: ${color};
    }
    &:hover {
      color: ${color};
      fill: inherit;
    }

    &${HOVER_FROM_CLEAR} {
      color: ${color};
      fill: inherit;
    }
  `;
};

// Border, background and text for a pill state; shared with anything pill-like but differently shaped.
export const pillStateStyles = css<PillState>`
  cursor: ${({ $enabled, $loading }) => ($loading || $enabled === false ? 'not-allowed' : 'pointer')};
  ${getBorderStyles}
  ${getBackgroundStyles}
  ${getTextStyles}
`;

const customStylesForSpecificFilterPills = css<PillState>`
  &.pill-sort button:not([data-pill-clear]) {
    border-radius: 8px;
    padding: 10px 16px;
    gap: 8px;

    /* The sort icon sat 2px clear of the label on top of that gap, so the pill keeps its width. */
    > svg,
    > ${IconGlyph} {
      margin-right: 2px;
    }

    > span:first-of-type {
      padding: 0;
    }

    /* This pill draws a wider chevron than the rest of the bar. v59 owns the element, so the glyph is
       repainted over it rather than scaled, which would thicken the strokes along with the span. */
    > span:last-child:not(:first-of-type) {
      width: 16px;
      height: 16px;
      background-color: currentColor;
      mask: url('${starfishCssUrl('Arrows', 'icon-chevron-down')}') no-repeat center / 100% 100%;
      -webkit-mask: url('${starfishCssUrl('Arrows', 'icon-chevron-down')}') no-repeat center / 100% 100%;
      ${({ $open }) => $open && 'rotate: 180deg;'}

      > svg {
        display: none;
      }
    }
  }
`;

/** The selected count as the design's filled badge, in place of the design system's "(n)". */
export const PillCountBadge = styled.span`
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  background-color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
  color: ${({ theme }) => theme.kallisto.color.text.static.textBrandContrastPrimary};
`;

export const PillCustomWrapper = styled.div<
  PillState & { $iconOnly?: boolean; $iconAlone?: boolean; $removes?: boolean }
>`
  display: inline-flex;
  flex-shrink: 0;
  position: relative;
  ${({ $removes }) => $removes && 'max-width: 100%;'}

  /* Doubled deliberately: the design system states itself at (0,3,0), so a plain descendant loses. */
  && button:not([data-pill-clear]) {
    padding: 8px 11px;
    height: 40px;
    border-radius: ${({ theme }) => theme.kallisto.border.radius.radius400};
    gap: 2px;
    transition: none;
    /* A pill keeps its label on one line and its full width; the bar scrolls to reach it. */
    white-space: nowrap;

    ${({ $clears }) =>
      $clears &&
      css`
        padding-right: 37px;
      `}

    ${({ $loading, $iconOnly }) =>
      $loading &&
      css`
        width: ${$iconOnly ? 52 : 102}px;
      `}

    ${pillStateStyles}

    &:focus-visible {
      border-color: transparent;
      box-shadow: unset;
      ${focusRing};
    }

    /* The leading icon, whether a Starfish glyph or a raw svg. */
    > svg,
    > ${IconGlyph} {
      width: 20px;
      height: 20px;
    }

    /* Label content that sizes itself, as a design system Text does, still reads as a pill. */
    span {
      ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};
    }

    /* The label carries its own breathing room, so a pill with nothing beside it keeps its width. */
    > span:first-of-type {
      padding: 0 2px;
    }

    /* That breathing room sat between the label and its count, not after the count, so it moves there. */
    > span:first-of-type:has([class*='Pill__Count']) {
      padding-right: 0;
      gap: 4px;
    }

    > span:first-of-type:has(${PillCountBadge}) {
      gap: ${({ theme }) => theme.kallisto.space.space50};
    }

    ${PillCountBadge} {
      ${({ theme }) => theme.kallisto.mixins.font('Caption')};
      font-weight: ${({ theme }) => theme.kallisto.font.weight.bold};
    }

    /* The design's icon-only pill is 52px: the icon and its padding, with no label slot beside it. */
    ${({ $iconAlone }) =>
      $iconAlone &&
      css`
        padding: 8px 15px;
        gap: 0;

        > span:first-of-type {
          padding: 0;
        }
      `}

    /* The design system pairs its clear icon with the selected state; a plain chip has nothing to clear. */
    ${({ $hideStateIcon }) =>
      $hideStateIcon &&
      css`
        > span:last-child:not(:first-of-type) {
          display: none;
        }
      `}

    /* A picked name can outgrow its row, so the chip stops at the row's edge and its label ends in an ellipsis. */
    ${({ $removes }) =>
      $removes &&
      css`
        max-width: 100%;

        > span:first-of-type {
          display: block;
          ${ellipsis};
        }
      `}

    /* v59 clears with an 18px legacy glyph; the design's is the 20px Starfish one, repainted over it. */
    ${({ $removes }) =>
      $removes &&
      css`
        > span:last-child:not(:first-of-type) {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          background-color: currentColor;
          mask: url('${starfishCssUrl('Semantics', 'icon-circle-solid-cross')}') no-repeat center / 100% 100%;
          -webkit-mask: url('${starfishCssUrl('Semantics', 'icon-circle-solid-cross')}') no-repeat center / 100% 100%;

          > svg {
            display: none;
          }
        }
      `}
  }

  ${customStylesForSpecificFilterPills};
`;

/** The clear control prod put inside a selected pill; v59's Pill is a single button and has no slot for it. */
export const PillClearButton = styled.button<{ $disabled?: boolean }>`
  position: absolute;
  right: 11px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  /* v59 colours its own clear icon with the pill's text colour, where v54 hardcoded a grey. */
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};

  &:hover {
    color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryHover};
  }

  &[aria-disabled='true'] {
    cursor: not-allowed;
  }

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      &,
      &:hover {
        color: ${theme.kallisto.color.text.static.textDisabled};
      }
    `}

  /* Round, not the pill's radius: the target is a 22px circular glyph. */
  &:focus-visible {
    ${focusRing};
    border-radius: 50%;
  }
`;

export const pillTrailingSpace = css`
  ${PillCustomWrapper} {
    margin-right: ${({ theme }) => theme.kallisto.space.space100};
  }

  .last ${PillCustomWrapper} {
    margin-right: 0;
  }
`;

/** Keeps a skeletoned pill's name in the accessibility tree, out of sight. */
export const PillHiddenLabel = styled.span`
  ${srOnly};
`;
